import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Plus, Trash2, X } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";

const emptyLine = { product: null, qty: 1 };

const normalizeIntegerInput = (value) => {
    const digitsOnly = String(value).replace(/\D/g, "");
    return digitsOnly === "" ? "" : Number(digitsOnly);
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#062b4f" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 1px #062b4f" : "none",
        "&:hover": {
            borderColor: state.isFocused ? "#062b4f" : "#cbd5e1",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "2px 8px",
    }),
    menu: (base) => ({
        ...base,
        zIndex: 60,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 60,
    }),
};

export default function AdminPreReleaseModal({
    isOpen,
    onClose,
    onSubmit,
    branches,
    loading,
}) {
    const [branch, setBranch] = useState(null);
    const [products, setProducts] = useState([]);
    const [lines, setLines] = useState([{ ...emptyLine }]);
    const [remarks, setRemarks] = useState("");
    const [productLoading, setProductLoading] = useState(false);
    const [error, setError] = useState("");
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setBranch(null);
        setProducts([]);
        setLines([{ ...emptyLine }]);
        setRemarks("");
        setError("");
        setConfirmAction(null);
    }, [isOpen]);

    useEffect(() => {
        if (!branch?.value) {
            setProducts([]);
            setLines([{ ...emptyLine }]);
            return;
        }

        let cancelled = false;
        setProductLoading(true);
        axiosInstance.get(`/pos/all-products?branch_id=${branch.value}`)
            .then((res) => {
                if (!cancelled) setProducts(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                if (!cancelled) {
                    setProducts([]);
                    setError("Failed to load products for this branch.");
                }
            })
            .finally(() => {
                if (!cancelled) setProductLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [branch]);

    const branchOptions = useMemo(
        () => branches.map((b) => ({ value: String(b.id), label: b.name })),
        [branches]
    );

    useEffect(() => {
        if (!isOpen || branchOptions.length !== 1) return;
        setBranch(branchOptions[0]);
    }, [isOpen, branchOptions]);

    const selectedProductIds = lines
        .map((line) => line.product?.value)
        .filter(Boolean);

    const productOptions = products
        .filter((p) => Number(p.warehouse_stock || 0) > 0)
        .map((p) => ({
            value: String(p.id),
            label: p.name,
            stock: Number(p.warehouse_stock || 0),
        }));

    const updateLine = (index, nextLine) => {
        setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...nextLine } : line)));
    };

    const addLine = () => {
        setLines((prev) => [...prev, { ...emptyLine }]);
    };

    const removeLine = (index) => {
        setLines((prev) => (prev.length === 1 ? [{ ...emptyLine }] : prev.filter((_, i) => i !== index)));
    };

    const buildPayload = () => {
        setError("");
        if (!branch) {
            setError("Please select a branch.");
            return null;
        }

        const items = lines
            .filter((line) => line.product && Number.isInteger(Number(line.qty)) && Number(line.qty) > 0)
            .map((line) => ({
                product_id: Number(line.product.value),
                qty: Number(line.qty),
            }));

        if (items.length === 0) {
            setError("Please add at least one product.");
            return null;
        }

        const duplicateIds = new Set();
        const hasDuplicate = items.some((item) => {
            if (duplicateIds.has(item.product_id)) return true;
            duplicateIds.add(item.product_id);
            return false;
        });
        if (hasDuplicate) {
            setError("Each product can only be added once.");
            return null;
        }

        const overStock = items.find((item) => {
            const product = products.find((p) => Number(p.id) === item.product_id);
            return product && item.qty > Number(product.warehouse_stock || 0);
        });
        if (overStock) {
            const product = products.find((p) => Number(p.id) === overStock.product_id);
            setError(`${product?.name || "Product"} only has ${product?.warehouse_stock || 0} units in warehouse.`);
            return null;
        }

        return items;
    };

    const requestConfirmation = (approve) => {
        const items = buildPayload();
        if (!items) return;
        setConfirmAction({ approve, items });
    };

    const submitConfirmed = async () => {
        if (!confirmAction) return;
        const formData = new FormData();
        formData.append("branch_id", branch.value);
        formData.append("remarks", remarks);
        formData.append("items", JSON.stringify(confirmAction.items));
        formData.append("approve", confirmAction.approve ? "true" : "false");

        try {
            await onSubmit(formData, confirmAction.approve);
            setConfirmAction(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to submit pre-release.");
            setConfirmAction(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-2xl">
                <div className="flex items-start justify-between gap-4 bg-white px-6 pt-4 pb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Add Pre-Release</h2>
                        <p className="text-sm text-gray-500">Create a request, or submit and approve it immediately.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-5 px-6 py-5">
                    <div>
                        <div className="space-y-1.5">
                            <label className="block text-sm font-semibold text-gray-700">Branch</label>
                            <Select
                                styles={selectStyles}
                                options={branchOptions}
                                value={branch}
                                onChange={setBranch}
                                placeholder="Select branch..."
                                isSearchable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <label className="block text-sm font-semibold text-gray-700">Items</label>
                            <button
                                type="button"
                                onClick={addLine}
                                className="inline-flex items-center gap-2 rounded-md bg-[#062b4f] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0a3b68]"
                            >
                                <Plus size={16} />
                                Add Item
                            </button>
                        </div>

                        <div className="space-y-3">
                            {lines.map((line, index) => {
                                const selectedOptionIds = selectedProductIds.filter((id) => id !== line.product?.value);
                                const available = line.product?.stock ?? 0;
                                return (
                                    <div key={index} className="grid gap-3 rounded-lg border border-gray-200 p-3 md:grid-cols-[1fr_110px_44px]">
                                        <Select
                                            styles={selectStyles}
                                            options={productOptions}
                                            value={line.product}
                                            onChange={(value) => updateLine(index, { product: value, qty: 1 })}
                                            placeholder={productLoading ? "Loading products..." : "Select product..."}
                                            isDisabled={!branch || productLoading}
                                            isOptionDisabled={(option) => selectedOptionIds.includes(option.value)}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            formatOptionLabel={(option) => (
                                                <div className="flex items-center justify-between gap-3">
                                                    <span>{option.label}</span>
                                                    <span className="text-xs font-semibold text-gray-500">Stock: {option.stock}</span>
                                                </div>
                                            )}
                                        />
                                        <input
                                            type="number"
                                            step="1"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            min="1"
                                            max={available || undefined}
                                            value={line.qty}
                                            onKeyDown={(e) => {
                                                if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                                            }}
                                            onPaste={(e) => {
                                                const pasted = e.clipboardData.getData("text");
                                                if (!/^\d+$/.test(pasted)) e.preventDefault();
                                            }}
                                            onChange={(e) => updateLine(index, { qty: normalizeIntegerInput(e.target.value) })}
                                            className="h-[38px] rounded-md border border-gray-300 px-3 text-sm focus:border-[#062b4f] focus:outline-none"
                                            aria-label="Quantity"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeLine(index)}
                                            className="flex h-[38px] items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600"
                                            aria-label="Remove item"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700">Remarks</label>
                        <input
                            type="text"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            maxLength={200}
                            placeholder="Remarks (optional)"
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#062b4f] focus:outline-none"
                        />
                    </div>

                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
                            {error}
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t bg-white px-6 py-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => requestConfirmation(false)}
                        disabled={loading}
                        className="rounded-md bg-[#062b4f] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a3b68] disabled:opacity-60"
                    >
                        {loading ? "Submitting..." : "Submit"}
                    </button>
                    <button
                        type="button"
                        onClick={() => requestConfirmation(true)}
                        disabled={loading}
                        className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                    >
                        {loading ? "Processing..." : "Submit and Approve"}
                    </button>
                </div>
            </div>
            {confirmAction && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true">
                    <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-2xl">
                        <h3 className="text-lg font-bold text-gray-900">
                            {confirmAction.approve ? "Submit and Approve?" : "Submit Pre-Release?"}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600">
                            {confirmAction.approve
                                ? "This will create the pre-release and immediately approve the stock transfer."
                                : "This will create a pending pre-release request."}
                        </p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmAction(null)}
                                disabled={loading}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitConfirmed}
                                disabled={loading}
                                className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${confirmAction.approve ? "bg-green-600 hover:bg-green-700" : "bg-[#062b4f] hover:bg-[#0a3b68]"}`}
                            >
                                {loading ? "Processing..." : confirmAction.approve ? "Submit and Approve" : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
